<div class="role19-610000-form-container">
  <p-toast></p-toast>

  <app-reload-banner [visible]="reloadPromptDismissed()" (reload)="onReload()" />

  <app-sheet-loader [visible]="isLoading()" />

  <p-card [header]="formTitle" [subheader]="isDev ? schema.label : ''" *ngIf="!isLoading() && schema" class="form-card">

    <div
      class="bg-gray-200 text-black-700 text-sm font-medium px-2 py-1 mt-3 mb-3"
    >
      Disclosure of changes in equity
    </div>

    <!-- Debug-layout swap spinner. Rendered while the toggle service is
         transitioning so the user sees feedback instead of a frozen page
         while ~thousands of p-inputNumber instances are torn down / built. -->
    <app-sheet-loader
      [visible]="debugToggle.transitioning() && hasEquityColumns()"
      label="Switching layout…" />

    <!-- Refactored developer debug layout (non-prod only, Ctrl+Alt+D to toggle).
         Renders in place of the original 4-table grid; Submit/Cancel stay below. -->
    <app-role19-debug-layout
      *ngIf="!envService.isProduction() && debugToggle.effectiveEnabled() && !debugToggle.transitioning() && hasEquityColumns() && statementOfChangesInEquityForm"
      [form]="statementOfChangesInEquityForm"
      [schema]="schema"
      [host]="this">
    </app-role19-debug-layout>

    <!-- Equity Columns Mode: Render 4 Tables (Consolidated/Separate x Current/Previous) -->
    <ng-container *ngIf="!(debugToggle.effectiveEnabled() && !envService.isProduction()) && !debugToggle.transitioning() && hasEquityColumns() && statementOfChangesInEquityForm">
      <!-- Ctrl+Alt+T column-compression status bar (non-prod only). Shows the
           current window of amount columns and the paging chords. The Error
           Description column is widened (see .column-compressed in the CSS). -->
      <div *ngIf="isColumnCompressionActive()" class="column-compression-bar">
        <span class="cc-label">
          Column compression on — showing
          <strong>{{ debugColumnPaging.windowStart() + 1 }}–{{ debugColumnPaging.windowStart() + visibleEquityColumns().length }}</strong>
          of {{ schema.equityColumns?.length || 0 }} amount columns
          (page {{ debugColumnPaging.pageIndex() + 1 }} / {{ debugColumnPaging.pageCount() }})
        </span>
        <span class="cc-hint">Ctrl+Alt+1 prev · Ctrl+Alt+2 next · Ctrl+Alt+T off</span>
      </div>

      <!-- Table 1: Consolidated - Current Year -->
      <div class="financial-statement-table-container viewport-sticky" *ngIf="showConsolidatedColumns">
        <table
          class="financial-statement-table viewport-sticky-header"
          [class.column-compressed]="isColumnCompressionActive()"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-changes-in-equity"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="expandedSections.get('Consolidated_Current')"
          [fieldLinkIsSectionHeader]="isSectionHeaderFn"
        >
          <ng-container *ngTemplateOutlet="equityTableTemplate; context: { consolidationType: 'Consolidated', period: periodHeaderCurrent, periodType: 'Current', formGroup: statementOfChangesInEquityForm }"></ng-container>
        </table>
      </div>

      <!-- Table 2: Consolidated - Previous Year -->
      <div class="financial-statement-table-container viewport-sticky" *ngIf="showConsolidatedColumns && hasPreviousYear">
        <table
          class="financial-statement-table viewport-sticky-header"
          [class.column-compressed]="isColumnCompressionActive()"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-changes-in-equity"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="expandedSections.get('Consolidated_Previous')"
          [fieldLinkIsSectionHeader]="isSectionHeaderFn"
        >
          <ng-container *ngTemplateOutlet="equityTableTemplate; context: { consolidationType: 'Consolidated', period: periodHeaderPrevious, periodType: 'Previous', formGroup: statementOfChangesInEquityForm }"></ng-container>
        </table>
      </div>

      <!-- Table 3: Separate - Current Year -->
      <div class="financial-statement-table-container viewport-sticky">
        <table
          class="financial-statement-table viewport-sticky-header"
          [class.column-compressed]="isColumnCompressionActive()"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-changes-in-equity"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="expandedSections.get('Separate_Current')"
          [fieldLinkIsSectionHeader]="isSectionHeaderFn"
        >
          <ng-container *ngTemplateOutlet="equityTableTemplate; context: { consolidationType: 'Separate', period: periodHeaderCurrent, periodType: 'Current', formGroup: statementOfChangesInEquityForm }"></ng-container>
        </table>
      </div>

      <!-- Table 4: Separate - Previous Year -->
      <div class="financial-statement-table-container viewport-sticky" *ngIf="hasPreviousYear">
        <table
          class="financial-statement-table viewport-sticky-header"
          [class.column-compressed]="isColumnCompressionActive()"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-changes-in-equity"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="expandedSections.get('Separate_Previous')"
          [fieldLinkIsSectionHeader]="isSectionHeaderFn"
        >
          <ng-container *ngTemplateOutlet="equityTableTemplate; context: { consolidationType: 'Separate', period: periodHeaderPrevious, periodType: 'Previous', formGroup: statementOfChangesInEquityForm }"></ng-container>
        </table>
      </div>
    </ng-container>

    <form [formGroup]="statementOfChangesInEquityForm" *ngIf="!(debugToggle.effectiveEnabled() && !envService.isProduction()) && !debugToggle.transitioning() && !hasEquityColumns() && statementOfChangesInEquityForm">

      <!-- Dual Column Mode: Single Table (Legacy) -->
      <div class="financial-statement-table-container viewport-sticky" *ngIf="!hasEquityColumns()">
        <table
          class="financial-statement-table viewport-sticky-header"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-changes-in-equity"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="expandedSections.get('Separate_Current')"
          [fieldLinkIsSectionHeader]="isSectionHeaderFn"
        >
          <thead>
            <!-- Dual Column Mode Header (Legacy) -->
            <ng-container *ngIf="!hasEquityColumns()">
              <!-- Top merged header row -->
              <tr>
                <th class="description-column" rowspan="2">Fields</th>
                <th class="amount-column merged-header" [attr.colspan]="hasPreviousYear ? 2 : 1" *ngIf="showConsolidatedColumns">Consolidated</th>
                <th class="amount-column merged-header" [attr.colspan]="hasPreviousYear ? 2 : 1">Separate</th>
                <th class="actions-column" rowspan="2">Actions</th>
                <th class="validation-status-column" rowspan="2">Mapping Status</th>
                <th class="validation-error-column" rowspan="2">Error Description</th>
              </tr>
              <!-- Individual column headers -->
              <tr>
                <th class="amount-column" *ngIf="showConsolidatedColumns">
                  <div>{{ periodHeaderCurrent }}</div>
                  <div>(Current)</div>
                </th>
                <th class="amount-column" *ngIf="showConsolidatedColumns && hasPreviousYear">
                  <div>{{ periodHeaderPrevious }}</div>
                  <div>(Previous)</div>
                </th>
                <th class="amount-column">
                  <div>{{ periodHeaderCurrent }}</div>
                  <div>(Current)</div>
                </th>
                <th class="amount-column" *ngIf="hasPreviousYear">
                  <div>{{ periodHeaderPrevious }}</div>
                  <div>(Previous)</div>
                </th>
              </tr>
            </ng-container>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema.controls; let controlIndex = index">
              <!-- Section Headers -->
              <tr *ngIf="isSectionHeader(control) && shouldShowRow(control, controlIndex, 'Separate', 'Current')"
                  [ngClass]="getHeaderClass(control.ui.level)"
                  (click)="isCollapsibleHeader(controlIndex) ? toggleSection(control.qname, 'Separate', 'Current') : null">
                <td [attr.colspan]="(showConsolidatedColumns ? (hasPreviousYear ? 8 : 6) : (hasPreviousYear ? 6 : 5))" class="section-header">
                  <h3>
                    <i *ngIf="isCollapsibleHeader(controlIndex)"
                       [class]="isSectionExpanded(control.qname, 'Separate', 'Current') ? 'pi pi-chevron-down' : 'pi pi-chevron-right'">
                    </i>
                    {{ control.label }}
                  </h3>
                </td>
              </tr>

              <!-- Dual Column Mode - Monetary Fields (Legacy, 4 Columns with Dynamic Visibility) -->
              <tr *ngIf="control.ui.controlType === 'number' && control.ui.dualColumn && !isSectionHeader(control) && shouldShowRow(control, controlIndex, 'Separate', 'Current')"
                  class="data-row"
                  [attr.data-qname]="control.qname"
                  [class.calculated-row]="isCalculatedField(control)"
                  [class.row-has-error]="getRowColorIntent(control) === 'error'"
                  [class.row-has-validation-error]="getRowColorIntent(control) === 'warning'">
                <td class="description-column">
                  <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
                </td>
                <td class="amount-column" *ngIf="showConsolidatedColumns"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + '[Consolidated][CurrentYear]'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [style]="{'width': '100%'}" [ngClass]="{'has-equity-beginning-end-error': hasEquityBeginningEndErrorDual(control, '[Consolidated][CurrentYear]')}"></p-inputnumber></td>
                <td class="amount-column" *ngIf="showConsolidatedColumns && hasPreviousYear"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + '[Consolidated][PreviousYear]'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [style]="{'width': '100%'}" [ngClass]="{'has-equity-beginning-end-error': hasEquityBeginningEndErrorDual(control, '[Consolidated][PreviousYear]')}"></p-inputnumber></td>
                <td class="amount-column"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + '[Separate][CurrentYear]'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [style]="{'width': '100%'}" [ngClass]="{'has-equity-beginning-end-error': hasEquityBeginningEndErrorDual(control, '[Separate][CurrentYear]')}"></p-inputnumber></td>
                <td class="amount-column" *ngIf="hasPreviousYear"><p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight [formControlName]="control.qname + '[Separate][PreviousYear]'" mode="decimal" [minFractionDigits]="numericPolicy(control).decimals" [maxFractionDigits]="numericPolicy(control).decimals" [useGrouping]="numericPolicy(control).grouping" [style]="{'width': '100%'}" [ngClass]="{'has-equity-beginning-end-error': hasEquityBeginningEndErrorDual(control, '[Separate][PreviousYear]')}"></p-inputnumber></td>

                <!-- Actions Column - Edit/Save/Cancel Icons -->
                <td class="actions-column">
                  <ng-container *ngIf="!isCalculatedField(control) && !control.ui.readonly">
                    <!-- Pencil SVG Icon (Default - Not in Edit Mode) -->
                    <svg *ngIf="isSyncValidationComplete() &&!isRowInEditMode(control)"
                         class="action-icon pencil-icon"
                         viewBox="0 0 16 16"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg"
                         (click)="enterEditMode(control)"
                         title="Edit">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12.1657 1.94834C12.3339 1.77238 12.5621 1.67352 12.8 1.67352C12.9178 1.67352 13.0345 1.69779 13.1433 1.74494C13.2521 1.7921 13.351 1.86121 13.4343 1.94834C13.5176 2.03547 13.5837 2.1389 13.6288 2.25274C13.6739 2.36657 13.6971 2.48858 13.6971 2.6118C13.6971 2.73502 13.6739 2.85703 13.6288 2.97086C13.5837 3.0847 13.5176 3.18814 13.4343 3.27526L3.59101 13.5709L1.89951 14.0132L2.32239 12.244L12.1657 1.94834ZM12.8 0C12.1377 0 11.5026 0.275171 11.0343 0.764979L1.03433 11.2245C0.931804 11.3317 0.859069 11.4661 0.823902 11.6132L0.0239029 14.9603C-0.044252 15.2454 0.0356272 15.5471 0.234331 15.7549C0.433035 15.9627 0.721425 16.0463 0.994044 15.975L4.19404 15.1382C4.33471 15.1015 4.46317 15.0254 4.5657 14.9181L14.5657 4.45862C14.7976 4.21609 14.9815 3.92817 15.107 3.61129C15.2325 3.29441 15.2971 2.95479 15.2971 2.6118C15.2971 2.26881 15.2325 1.92919 15.107 1.61231C14.9815 1.29543 14.7976 1.00751 14.5657 0.764979C14.3338 0.52245 14.0585 0.330067 13.7556 0.198811C13.4526 0.0675562 13.1279 0 12.8 0ZM8.00001 14.3265C7.55818 14.3265 7.20001 14.7011 7.20001 15.1632C7.20001 15.6254 7.55818 16 8.00001 16H15.2C15.6418 16 16 15.6254 16 15.1632C16 14.7011 15.6418 14.3265 15.2 14.3265H8.00001Z"
                        fill="currentColor"
                      />
                    </svg>

                    <!-- Tick and Cross SVG Icons (In Edit Mode - Same Line) -->
                    <div *ngIf="isRowInEditMode(control)" class="action-icons-group">
                      <!-- Tick/Check SVG Icon -->
                      <svg class="action-icon tick-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="saveEditMode(control)"
                           title="Save">
                        <path
                          d="M13.5 3.5L6 11L2.5 7.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      <!-- Cross/X SVG Icon -->
                      <svg class="action-icon cross-icon"
                           viewBox="0 0 16 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           (click)="cancelEditMode(control)"
                           title="Cancel">
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </ng-container>
                </td>

                <!-- Mapping Status -->
                <td
                  class="validation-status-column"
                  [ngClass]="{
                    'status-failed': isSyncValidationComplete() &&getRowValidationStatus(control) === 'Failed',
                    'status-validated-warning': isSyncValidationComplete() &&getRowValidationStatus(control) === 'Validated*'
                  }"
                >
                  <ng-container *ngIf="isSyncValidationComplete(); else validatingDual">
                    <ng-container [ngSwitch]="getRowValidationStatus(control)">
                      <ng-container *ngSwitchCase="'Validated*'">
                        Validated<span class="status-asterisk">*</span>
                      </ng-container>
                      <ng-container *ngSwitchCase="'Failed'">
                        Failed
                      </ng-container>
                      <ng-container *ngSwitchDefault>
                        {{ getRowValidationStatus(control) }}
                      </ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-template #validatingDual>
                    <p-skeleton width="70%" height="1rem" />
                  </ng-template
                  ><app-required-marker [when]="isFieldRequired(control)" />
                </td>

                <!-- Error Description -->
                <td class="validation-error-column">
                  <ng-container *ngIf="isSyncValidationComplete(); else errorLoading">
                    <!-- Ctrl+Alt+E (non-prod only): compressed "X errors, Y warnings" summary -->
                    <ng-container *ngIf="!envService.isProduction() && debugErrorCompression.enabled(); else fullErrorsDual">
                      {{ formatCompressedErrors(getRowErrorCounts(control)) }}
                    </ng-container>
                    <ng-template #fullErrorsDual>
                      <span [innerHTML]="getRowErrorDescription(control)"></span>
                    </ng-template>
                  </ng-container>
                  <ng-template #errorLoading>
                    <p-skeleton width="100%" height="1rem" />
                  </ng-template
                  ><app-required-marker [when]="isFieldRequired(control)" />
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
    </form>
     <submit-for-validation
      [form]="statementOfChangesInEquityForm"
      [sheetKey]="'statementOfChangesInEquity'"
      [formTitle]="formTitle"
      [evaluateSheetStatus]="evaluateSheetStatus.bind(this)"
      [externallyDisabled]="reloadPromptDismissed()"
      [externallyDisabledReason]="reloadPromptDismissed() ? 'Please refresh the page to obtain the latest version of this sheet.' : undefined"
      (cancelConfirmed)="onCancelConfirmed()"
    ></submit-for-validation>
  </p-card>

  <!-- Template for Equity Tables -->
  <ng-template #equityTableTemplate let-consolidationType="consolidationType" let-period="period" let-periodType="periodType" let-formGroup="formGroup">
    <thead>
      <!-- Row 1: Consolidation Type -->
      <tr>
        <th class="description-column" rowspan="3"></th>
        <th class="equity-amount-column merged-header" [attr.colspan]="visibleEquityColumns().length">
          {{ consolidationType }}
        </th>
        <th class="actions-column" rowspan="3">Actions</th>
        <th class="validation-status-column" rowspan="3">Mapping Status</th>
        <th class="validation-error-column" rowspan="3">Error Description</th>
      </tr>
      <!-- Row 2: Period -->
      <tr>
        <th class="equity-amount-column merged-header" [attr.colspan]="visibleEquityColumns().length">
          <div>{{ period }}</div>
          <div>({{ periodType }})</div>
        </th>
      </tr>
      <!-- Row 3: Equity Column Names -->
      <tr>
        <th class="equity-amount-column" *ngFor="let column of visibleEquityColumns()">
          {{ column.label }}
        </th>
      </tr>
    </thead>
    <tbody [formGroup]="formGroup">
      <ng-container *ngFor="let control of schema?.controls; let controlIndex = index">
        <!-- Section Headers -->
        <tr *ngIf="isSectionHeader(control) && shouldShowRow(control, controlIndex, consolidationType, periodType)"
            [ngClass]="getHeaderClass(control.ui.level)"
            (click)="isCollapsibleHeader(controlIndex) ? toggleSection(control.qname, consolidationType, periodType) : null">
          <td [attr.colspan]="visibleEquityColumns().length + 4" class="section-header">
            <h3>
              <i *ngIf="isCollapsibleHeader(controlIndex)"
                 [class]="isSectionExpanded(control.qname, consolidationType, periodType) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'">
              </i>
              {{ control.label }}
            </h3>
          </td>
        </tr>

        <!-- Equity Columns Mode - Monetary Fields -->
        <tr *ngIf="control.ui.controlType === 'number' && control.ui.equityColumns && !isSectionHeader(control) && shouldShowRow(control, controlIndex, consolidationType, periodType)"
            class="data-row"
            [attr.data-qname]="control.qname"
            [class.calculated-row]="isCalculatedField(control)"
            [class.row-has-error]="getRowColorIntent(control, consolidationType, periodType) === 'error'"
            [class.row-has-validation-error]="getRowColorIntent(control, consolidationType, periodType) === 'warning'">
          <td class="description-column">
            <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
          </td>
          <td class="equity-amount-column" *ngFor="let column of visibleEquityColumns()">
            <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
              [formControlName]="getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType)"
              mode="decimal"
              [minFractionDigits]="numericPolicy(control).decimals"
              [maxFractionDigits]="numericPolicy(control).decimals"
              [useGrouping]="numericPolicy(control).grouping"
              [style]="{'width': '100%'}"
              [ngClass]="{
                'has-cross-sheet-error': hasCrossSheetError(control.qname, column.qname, consolidationType, periodType),
                'has-required-error': hasRequiredError(control, column.qname, consolidationType, periodType),
                'has-non-distributable-reserves-error': hasColumnSumError(control, column.qname, consolidationType, periodType),
                'has-equity-total-error': hasEquityTotalError(control, column.qname, consolidationType, periodType),
                'has-equity-beginning-end-error': hasEquityBeginningEndError(control, column.qname, consolidationType, periodType)
              }">
            </p-inputnumber>
          </td>

          <!-- Actions Column - Edit/Save/Cancel Icons -->
          <td class="actions-column">
            <ng-container *ngIf="!isCalculatedField(control) && !control.ui.readonly">
              <!-- Pencil SVG Icon (Default - Not in Edit Mode) -->
              <svg *ngIf="isSyncValidationComplete() &&!isRowInEditMode(control, consolidationType, periodType)"
                   class="action-icon pencil-icon"
                   viewBox="0 0 16 16"
                   fill="none"
                   xmlns="http://www.w3.org/2000/svg"
                   (click)="enterEditMode(control, consolidationType, periodType)"
                   title="Edit">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12.1657 1.94834C12.3339 1.77238 12.5621 1.67352 12.8 1.67352C12.9178 1.67352 13.0345 1.69779 13.1433 1.74494C13.2521 1.7921 13.351 1.86121 13.4343 1.94834C13.5176 2.03547 13.5837 2.1389 13.6288 2.25274C13.6739 2.36657 13.6971 2.48858 13.6971 2.6118C13.6971 2.73502 13.6739 2.85703 13.6288 2.97086C13.5837 3.0847 13.5176 3.18814 13.4343 3.27526L3.59101 13.5709L1.89951 14.0132L2.32239 12.244L12.1657 1.94834ZM12.8 0C12.1377 0 11.5026 0.275171 11.0343 0.764979L1.03433 11.2245C0.931804 11.3317 0.859069 11.4661 0.823902 11.6132L0.0239029 14.9603C-0.044252 15.2454 0.0356272 15.5471 0.234331 15.7549C0.433035 15.9627 0.721425 16.0463 0.994044 15.975L4.19404 15.1382C4.33471 15.1015 4.46317 15.0254 4.5657 14.9181L14.5657 4.45862C14.7976 4.21609 14.9815 3.92817 15.107 3.61129C15.2325 3.29441 15.2971 2.95479 15.2971 2.6118C15.2971 2.26881 15.2325 1.92919 15.107 1.61231C14.9815 1.29543 14.7976 1.00751 14.5657 0.764979C14.3338 0.52245 14.0585 0.330067 13.7556 0.198811C13.4526 0.0675562 13.1279 0 12.8 0ZM8.00001 14.3265C7.55818 14.3265 7.20001 14.7011 7.20001 15.1632C7.20001 15.6254 7.55818 16 8.00001 16H15.2C15.6418 16 16 15.6254 16 15.1632C16 14.7011 15.6418 14.3265 15.2 14.3265H8.00001Z"
                  fill="currentColor"
                />
              </svg>

              <!-- Tick and Cross SVG Icons (In Edit Mode - Same Line) -->
              <div *ngIf="isRowInEditMode(control, consolidationType, periodType)" class="action-icons-group">
                <!-- Tick/Check SVG Icon -->
                <svg class="action-icon tick-icon"
                     viewBox="0 0 16 16"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                     (click)="saveEditMode(control, consolidationType, periodType)"
                     title="Save">
                  <path
                    d="M13.5 3.5L6 11L2.5 7.5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <!-- Cross/X SVG Icon -->
                <svg class="action-icon cross-icon"
                     viewBox="0 0 16 16"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                     (click)="cancelEditMode(control, consolidationType, periodType)"
                     title="Cancel">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </ng-container>
          </td>

          <!-- Mapping Status -->
          <td
            class="validation-status-column"
            [ngClass]="{
              'status-failed': isSyncValidationComplete() &&getRowValidationStatus(control, consolidationType, periodType) === 'Failed',
              'status-validated-warning': isSyncValidationComplete() &&getRowValidationStatus(control, consolidationType, periodType) === 'Validated*'
            }"
          >
            <ng-container *ngIf="isSyncValidationComplete(); else validatingEquity">
              <ng-container [ngSwitch]="getRowValidationStatus(control, consolidationType, periodType)">
                <ng-container *ngSwitchCase="'Validated*'">
                  Validated<span class="status-asterisk">*</span>
                </ng-container>
                <ng-container *ngSwitchCase="'Failed'">
                  Failed
                </ng-container>
                <ng-container *ngSwitchDefault>
                  {{ getRowValidationStatus(control, consolidationType, periodType) }}
                </ng-container>
              </ng-container>
            </ng-container>
            <ng-template #validatingEquity>
              <p-skeleton width="70%" height="1rem" />
            </ng-template
                  ><app-required-marker [when]="isFieldRequired(control)" />
          </td>

          <!-- Error Description -->
          <td class="validation-error-column">
            <ng-container *ngIf="isSyncValidationComplete(); else errorLoadingEquity">
              <!-- Ctrl+Alt+E (non-prod only): compressed "X errors, Y warnings" summary -->
              <ng-container *ngIf="!envService.isProduction() && debugErrorCompression.enabled(); else fullErrorsEquity">
                {{ formatCompressedErrors(getRowErrorCounts(control, consolidationType, periodType)) }}
              </ng-container>
              <ng-template #fullErrorsEquity>
                <span [innerHTML]="getRowErrorDescription(control, consolidationType, periodType)"></span>
              </ng-template>
            </ng-container>
            <ng-template #errorLoadingEquity>
              <p-skeleton width="100%" height="1rem" />
            </ng-template
                  ><app-required-marker [when]="isFieldRequired(control)" />
          </td>
        </tr>
      </ng-container>
    </tbody>
  </ng-template>

  <app-reload-prompt
    [visible]="showReloadPrompt()"
    mode="return-or-stay"
    title="Filing regenerated"
    message="Core settings were just submitted. The data on this page is being regenerated — return to files list, or stay on this view (it may be out of date)."
    (reload)="onReload()"
    (dismiss)="onDismiss()"
    (returnToFiles)="onReturnToFiles()"
  ></app-reload-prompt>
</div>
